import { module } from 'modujs'
import { gsap, CustomEase } from 'gsap/all'
import { clamp, map } from "../utils/maths"
import { html } from "../utils/environment"
import { lazyLoadImage } from "../utils/image"

gsap.registerPlugin(CustomEase)
CustomEase.create('custom', '0.38, 0.005, 0.215, 1');

const DRAG_THRESHOLD_MOBILE = 60
const DRAG_THRESHOLD_DESKTOP = 120
const DRAG_LERP = .1

export default class extends module {
    constructor(m) {
        super(m)

        // Binding
        this.onResizeBind = this.onResize.bind(this)
        this.onFontsLoadedBind = this.onFontsLoaded.bind(this)
        this.onUpdateBind = this.onUpdate.bind(this)
        this.onDragStartBind = this.onDragStart.bind(this)
        this.onDragEndBind = this.onDragEnd.bind(this)
        this.onDragBind = this.onDrag.bind(this)

        // UI
        this.$el = this.el
        this.$wrapper = this.$('wrapper')[0]
        this.$slides = this.$('slide')

        // Events
        this.events = {
            click: {
                prev: 'onPrev',
                next: 'onNext'
            }
        }

        // Boolean
        this.isAnimating = false

        // Drag
        this.drag = {
            startX: 0,
            lastX: 0,
            oldX: 0,
            x: 0,
            smoothX: 0,
            lerp: DRAG_LERP,
            isStarted: false
        }

        // Distance
        this.distX = 0

        // Drag threshold
        this.dragThreshold = DRAG_THRESHOLD_DESKTOP;

        // Slides
        this.currentSlideIndex = 0
        this.slideWidth = 0
    }

    ///////////////
    // Lifecyle
    ///////////////
    init() {
        this.bindEvents()
        this.setDragThreshold()

        if (window.isFontsLoaded) {
            this.onFontsLoaded()
        }
    }

    destroy() {
        super.destroy()
        this.unbindEvents()
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        window.addEventListener("resizeEnd", this.onResizeBind)
        window.addEventListener("fontsLoaded", this.onFontsLoadedBind)

        // Desktop
        this.$wrapper.addEventListener("mousedown",  this.onDragStartBind)
        this.$wrapper.addEventListener("mouseup",    this.onDragEndBind)
        this.$wrapper.addEventListener("mouseleave", this.onDragEndBind)
        this.$wrapper.addEventListener("mousemove",  this.onDragBind)

        // Mobile
        this.$wrapper.addEventListener("touchstart", this.onDragStartBind, { passive: true })
        this.$wrapper.addEventListener("touchend",   this.onDragEndBind, { passive: true })
        this.$wrapper.addEventListener("touchleave", this.onDragEndBind, { passive: true })
        this.$wrapper.addEventListener("touchmove",  this.onDragBind, { passive: true })
    }

    unbindEvents() {
        window.removeEventListener("resizeEnd", this.onResizeBind)
        window.removeEventListener("fontsLoaded", this.onFontsLoadedBind)
        
        // Desktop
        this.$wrapper.removeEventListener("mousedown", this.onDragStartBind)
        this.$wrapper.removeEventListener("mouseup", this.onDragEndBind)
        this.$wrapper.removeEventListener("mouseleave", this.onDragEndBind)
        this.$wrapper.removeEventListener("mousemove", this.onDragBind)

        // Mobile
        this.$wrapper.removeEventListener("touchstart", this.onDragStartBind, { passive: true })
        this.$wrapper.removeEventListener("touchend", this.onDragEndBind, { passive: true })
        this.$wrapper.removeEventListener("touchleave", this.onDragEndBind, { passive: true })
        this.$wrapper.removeEventListener("touchmove", this.onDragBind, { passive: true })
    }

    ///////////////
    // Callbacks
    ///////////////
    onResize() {
        this.initCarousel()
    }

    onFontsLoaded() {
        this.initCarousel()
    }

    onDragStart(event) {
        if (this.isAnimating) return

        // Add draging class
        html.classList.add('is-dragging')

        const pointerEvent = event.type !== 'mousedown' ? (event.touches[0] || event.changedTouches[0]) : event;

        // Set initial drag value
        this.drag.isStarted = true
        this.drag.oldX = this.drag.x
        this.drag.startX = pointerEvent.clientX

        this.play()
    }

    onDrag(event) {
        if (!this.drag.isStarted) return;

        const pointerEvent = event.type !== 'mousemove' ? (event.touches[0] || event.changedTouches[0]) : event;

        // Get current drag value
        this.drag.x = this.drag.oldX + (pointerEvent.clientX - this.drag.startX)
        this.drag.lastX = pointerEvent.clientX
    }

    onDragEnd(event) {
        if (!this.drag.isStarted) return;
        
        // Set ending drag value
        this.drag.isStarted = false;

        // Remove draging class
        html.classList.remove('is-dragging')

        // Get distance between start and end
        this.distX = this.drag.lastX - this.drag.startX

        // Snap to next / prev position
        if (this.distX > this.dragThreshold) {
            this.goToPrevSlide()
        } else if (this.distX < this.dragThreshold * -1) {
            this.goToNextSlide()
        } else {
            this.goToCurrentSlide()
        }
    }

    onUpdate() {
        this.drag.smoothX += (this.drag.x - this.drag.smoothX) * this.drag.lerp
        this.drag.smoothX = ((100 * (this.drag.smoothX + 0.01)) | 0) / 100
        this.computeProgress(this.drag.smoothX)

        if (Math.round(this.drag.smoothX) === Math.round(this.drag.x) && !this.drag.isStarted) {
            this.rafIncrement++
            if (this.rafIncrement >= 30) {
                this.pause()
            }
        }        
    }

    onPrev() {
        this.animeToPrevSlide()
    }

    onNext() {
        this.animeToNextSlide()
    }

    inview() {
        let index = 0
        while (index < 2) {
            const image = this.$('image', this.$slides[index])[0]

            lazyLoadImage(image, null, () => {
                //callback
            })
            index++
        }
    }

    ///////////////
    // Methods
    ///////////////
    initCarousel() {
        if (!this.$slides) return
        
        // Store slide width
        this.slideWidth = this.$slides[0].offsetWidth

        this.setDragThreshold()
        this.onUpdate()
    }

    setDragThreshold() {
        if (window.innerWidth < 860) {
            this.dragThreshold = DRAG_THRESHOLD_MOBILE
        } else {
            this.dragThreshold = DRAG_THRESHOLD_DESKTOP
        }
    }

    goToPrevSlide() {
        if (this.currentSlideIndex > 0) {
            this.currentSlideIndex = this.currentSlideIndex - 1
        }

        this.setTranslate()
    }

    goToNextSlide() {
        if (!this.$slides) return
        
        if (this.currentSlideIndex < this.$slides.length - 1) {
            this.currentSlideIndex = this.currentSlideIndex + 1
        }

        this.setTranslate()

        if (this.$slides[this.currentSlideIndex + 1]) {
            const image = this.$('image', this.$slides[this.currentSlideIndex + 1])[0]
            lazyLoadImage(image, null, () => {
                //callback
            })
        }
    }

    goToCurrentSlide() {
        this.setTranslate()
    }

    animeToPrevSlide() {
        if (this.currentSlideIndex > 0) {
            this.currentSlideIndex = this.currentSlideIndex - 1
        }

        this.animeTranslate()
    }

    animeToNextSlide() {
        if (!this.$slides) return
        
        if (this.currentSlideIndex < this.$slides.length - 1) {
            this.currentSlideIndex = this.currentSlideIndex + 1
        }

        if (this.$slides[this.currentSlideIndex + 1]) {
            const image = this.$('image', this.$slides[this.currentSlideIndex + 1])[0]
            lazyLoadImage(image, null, () => {
                //callback
            })
        }

        this.animeTranslate()
    }

    setTranslate() {
        this.drag.x = this.slideWidth * this.currentSlideIndex * - 1
    }

    animeTranslate() {
        this.drag.isStarted = true
        this.drag.lerp = 1
        this.isAnimating = true
        this.play()

        gsap.to(this.drag, 
            {
                x: this.slideWidth * this.currentSlideIndex * -1,
                duration: .8,
                ease: 'custom',
                onComplete: () => {
                    this.drag.isStarted = false
                    this.drag.lerp = DRAG_LERP
                    this.isAnimating = false
                }
            }
        );
    }

    computeProgress(x) {
        const slideLength = this.$slides.length
        const clampedX = clamp(x, -this.slideWidth * (slideLength - 1), 0)
        const globalProgress = map(clampedX, 0, -this.slideWidth * slideLength, 0, 100)

        this.$el.style.setProperty("--index-progress", globalProgress + '%')

        let slideIndex = 0
        // For each slide
        while (slideIndex < slideLength) {

            const currentSlideProgress = clamp(clampedX, -this.slideWidth * (slideIndex + 1), 0) + (this.slideWidth * slideIndex)
            const nextSlideProgress = clamp(map(clampedX, -this.slideWidth * slideIndex, -this.slideWidth * (slideIndex + 1), 1, 0), 0, 1)

            // Translate
            const computedX = map(clamp(currentSlideProgress, 0, this.slideWidth), 0, this.slideWidth, 0, 100)
            this.$slides[slideIndex].style.setProperty("--mask-progress", computedX + '%')

            // Opacity
            const opacity = clamp(nextSlideProgress, 0, 1)
            this.$slides[slideIndex].style.opacity = opacity

            // Scale
            const scaleProgress = map(nextSlideProgress, 0, 1, 1, 1.2)
            const scale = clamp(scaleProgress, 1, 1.2)
            const imageContainer = this.$('image', this.$slides[slideIndex + 1])[0].parentNode
            imageContainer.style.transform = `scale3d(${scale}, ${scale}, 1)`
        
            // Next item
            slideIndex++ 
        }
    }

    // RAF
    ///////////////
    play() {
        if (this.isRafPlaying) return

        this.isRafPlaying = true
        this.rafIncrement = 0

        gsap.ticker.add(this.onUpdateBind)
    }

    pause() {
        if (!this.isRafPlaying) return

        this.isRafPlaying = false

        gsap.ticker.remove(this.onUpdateBind)
    }
}